import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
const SHARE_LINK_MAX_DAYS = 90;
const SHARE_LINK_PASSWORD_ATTEMPTS = 5;
const SHARE_LINK_LOCK_MINUTES = 15;
const SHARE_LINK_ACCESS_MINUTES = 30;
const SHARE_LINK_ACCESS_HEADER = "x-share-access";
const SHARE_LINK_STATUSES = ["active", "expired", "revoked", "exhausted"];
const ShareLinkStatusSchema = z.enum(SHARE_LINK_STATUSES);
const ShareLinkTargetSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("document"), documentId: IdSchema }),
  z.strictObject({ type: z.literal("folder"), folderId: IdSchema })
]);
const CreateShareLinkRequestSchema = z.strictObject({
  target: ShareLinkTargetSchema,
  label: z.string().trim().min(1).max(120).optional(),
  expiresInDays: z.int().min(1).max(SHARE_LINK_MAX_DAYS),
  /** 10 to 128 characters; never stored, only its Argon2id hash. */
  password: z.string().min(10).max(128).optional(),
  watermark: z.boolean(),
  downloadAllowed: z.boolean(),
  maxDownloads: z.int().min(1).max(1e3).optional()
}).refine((v) => v.downloadAllowed || v.maxDownloads === void 0, {
  path: ["maxDownloads"],
  message: "a download limit needs downloads to be allowed"
});
const ShareLinkSchema = z.strictObject({
  id: IdSchema,
  spaceId: IdSchema,
  target: ShareLinkTargetSchema,
  /** The document's title or the folder's name now. */
  targetName: z.string(),
  label: z.string().nullable(),
  status: ShareLinkStatusSchema,
  expiresAt: IsoDateTimeSchema,
  hasPassword: z.boolean(),
  watermark: z.boolean(),
  downloadAllowed: z.boolean(),
  maxDownloads: z.int().nullable(),
  downloads: z.int().min(0),
  createdBy: z.strictObject({ id: IdSchema, name: z.string() }),
  createdAt: IsoDateTimeSchema,
  revokedAt: IsoDateTimeSchema.nullable(),
  lastAccessedAt: IsoDateTimeSchema.nullable()
});
const CreatedShareLinkSchema = z.strictObject({
  link: ShareLinkSchema,
  url: z.url()
});
const ShareLinkListQuerySchema = z.strictObject({
  documentId: IdSchema.optional(),
  folderId: IdSchema.optional()
});
const ShareLinkListSchema = z.strictObject({ items: z.array(ShareLinkSchema).max(500) });
const ShareLinkParamsSchema = z.strictObject({ spaceId: IdSchema, linkId: IdSchema });
const PublicShareLinkParamsSchema = z.strictObject({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/)
});
const PublicShareLinkDocumentParamsSchema = z.strictObject({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  documentId: IdSchema
});
const PublicShareDocumentSchema = z.strictObject({
  id: IdSchema,
  title: z.string(),
  code: z.string().nullable(),
  revisionIndex: z.string().nullable(),
  fileName: z.string(),
  sizeBytes: z.int().min(0)
});
const PublicShareLinkSchema = z.strictObject({
  organizationName: z.string(),
  label: z.string().nullable(),
  expiresAt: IsoDateTimeSchema,
  passwordRequired: z.boolean(),
  /** True while the password has not been given. */
  locked: z.boolean(),
  targetType: z.enum(["document", "folder"]),
  targetName: z.string().nullable(),
  documents: z.array(PublicShareDocumentSchema).max(500),
  watermark: z.boolean(),
  downloadAllowed: z.boolean(),
  /** Null: no limit. */
  downloadsLeft: z.int().min(0).nullable(),
  /** The viewer offers printing only when the space allows it (a hint; DLP is server-side). */
  printAllowed: z.boolean()
});
const UnlockShareLinkRequestSchema = z.strictObject({
  password: z.string().min(1).max(128)
});
const ShareLinkAccessSchema = z.strictObject({
  access: z.string(),
  expiresAt: IsoDateTimeSchema
});
export {
  CreateShareLinkRequestSchema,
  CreatedShareLinkSchema,
  PublicShareDocumentSchema,
  PublicShareLinkDocumentParamsSchema,
  PublicShareLinkParamsSchema,
  PublicShareLinkSchema,
  SHARE_LINK_ACCESS_HEADER,
  SHARE_LINK_ACCESS_MINUTES,
  SHARE_LINK_LOCK_MINUTES,
  SHARE_LINK_MAX_DAYS,
  SHARE_LINK_PASSWORD_ATTEMPTS,
  SHARE_LINK_STATUSES,
  ShareLinkAccessSchema,
  ShareLinkListQuerySchema,
  ShareLinkListSchema,
  ShareLinkParamsSchema,
  ShareLinkSchema,
  ShareLinkStatusSchema,
  ShareLinkTargetSchema,
  UnlockShareLinkRequestSchema
};
//# sourceMappingURL=share-links.js.map